/**
 * StoryHub API connector for managing posted stories
 * and media asset highlights in the shared dashboard
 */

import api from '@/config/api'
import catchErrors from './catchErrors'

export default {
  // Fetch StoryHub dashboard data
  getStoryHubData(params = {}) {
    return catchErrors.get('/v2/newsdesk/storyhub', {
      params,
      cache: {
        maxAge: 30 * 1000 // 30 second cache
      }
    })
  },

  // Posted stories endpoints
  getPostedStories(params = {}) {
    return catchErrors.get('/v2/newsdesk/storyhub', {
      params: {
        ...params,
        status: 'posted'
      },
      cache: {
        maxAge: 30 * 1000
      }
    })
  },

  // Get single story from StoryHub
  async getStoryHubStory(id) {
    const resp = await catchErrors.get(`/v2/newsdesk/storyhub/stories/${id}`)
    return resp?.data?.story ?? null
  },

  postStory(storyId, data = {}) {
    return api.post(`/v2/newsdesk/stories/${storyId}/post`, data)
  },

  unpostStory(storyId) {
    return api.delete(`/v2/newsdesk/stories/${storyId}/unpost`)
  },

  // Media asset posting
  postMediaAsset(mediaAssetId, data = {}) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/post`, data)
  },

  // Media asset highlight management
  setMediaAssetAsHighlight(mediaAssetId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/set-highlight`)
  },

  removeMediaAssetHighlight(mediaAssetId) {
    return api.delete(`/v2/newsdesk/media_assets/${mediaAssetId}/set-highlight`)
  },

  // Get highlight assets
  getHighlightAssets(params = {}) {
    return catchErrors.get('/v2/newsdesk/media_assets/highlights', {
      params,
      cache: {
        maxAge: 60 * 1000 // 1 minute cache
      }
    })
  },

  notifyStory(storyId) {
    return api.post(`/v2/newsdesk/storyhub/stories/${storyId}/notify`)
  }
}

// Export individual functions for use in store modules
export const storyHubApi = {
  // Dashboard data
  async fetchDashboardData(params = {}) {
    try {
      const response = await api.get('/v2/newsdesk/storyhub', { params })
      return response.data
    } catch (error) {
      console.error('StoryHub API Error:', error)
      throw error
    }
  },

  // Posted stories
  async fetchPostedStories(params = {}) {
    try {
      const response = await api.get('/v2/newsdesk/storyhub', {
        params: { ...params, status: 'posted' }
      })
      return response.data
    } catch (error) {
      console.error('Posted Stories API Error:', error)
      throw error
    }
  },

  async postStory(storyId, options = {}) {
    try {
      const response = await api.post(`/v2/newsdesk/stories/${storyId}/post`, options)
      return response.data
    } catch (error) {
      console.error('Post Story API Error:', error)
      throw error
    }
  },

  async unpostStory(storyId) {
    try {
      const response = await api.delete(`/v2/newsdesk/stories/${storyId}/unpost`)
      return response.data
    } catch (error) {
      console.error('Unpost Story API Error:', error)
      throw error
    }
  },

  // Media asset operations
  async postMediaAsset(mediaAssetId, options = {}) {
    try {
      const response = await api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/post`, options)
      return response.data
    } catch (error) {
      console.error('Post Media Asset API Error:', error)
      throw error
    }
  },

  async setAsHighlight(mediaAssetId) {
    try {
      const response = await api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/set-highlight`)
      return response.data
    } catch (error) {
      console.error('Set Highlight API Error:', error)
      throw error
    }
  },

  async removeHighlight(mediaAssetId) {
    try {
      const response = await api.delete(`/v2/newsdesk/media_assets/${mediaAssetId}/set-highlight`)
      return response.data
    } catch (error) {
      console.error('Remove Highlight API Error:', error)
      throw error
    }
  },

  // Script operations (extending existing story API)
  async updateStoryScript(storyId, scriptData) {
    try {
      const response = await api.patch(`/v2/newsdesk/stories/${storyId}`, {
        script: scriptData
      })
      return response.data
    } catch (error) {
      console.error('Update Script API Error:', error)
      throw error
    }
  }
}
